"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import { useClientSearchHook } from "../../hooks/useClientSearch";
import { useGrantAccessMutationHook } from "../../hooks/useGrantAccessMutation";
import { useGrantAccessForm } from "../../hooks/useGrantAccessForm";
import { GrantAccessEmployeeSection } from "../../components/GrantAccessEmployeeSection";
import { ClientsFieldSection } from "./ClientsFieldSection";
import type { ClientRecord } from "@/lib/types/auth/grant-access";

interface GrantEmployeeAccessFormProps {
  contactIds?: string[];
}

/**
 * Form component for use directly in ResponsiveModal
 * Used by GrantEmployeeAccessModalWrapper
 * Supports both single and bulk operations
 */
export const GrantEmployeeAccessForm: React.FC<GrantEmployeeAccessFormProps> = ({
  contactIds = [],
}) => {
  const { closeModal, isModalOpen } = useGlobalModalStack();
  const isOpen = isModalOpen("grant-employee-access");
  const isBulk = contactIds.length > 1;
  const singleContactId = contactIds.length === 1 ? contactIds[0] : undefined;

  const { form, resetForm, setContactIds, setGrantToAllClients, setUserId } =
    useGrantAccessForm(contactIds);

  const {
    clientSearchTerm,
    setClientSearchTerm,
    displayedClients,
    clientsLoading,
    isClientSearching,
  } = useClientSearchHook();

  const { grantAccessMutation, grantAccessToMultiple, revokeAccess } =
    useGrantAccessMutationHook();

  // Update contactIds when modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (contactIds.length > 0) {
        setContactIds(contactIds);
        setGrantToAllClients(false);
      }
    } else {
      resetForm();
    }
  }, [isOpen, contactIds, setContactIds, setGrantToAllClients, resetForm]);

  const handleRevokeAccess = React.useCallback(
    async (userId: string) => {
      if (!singleContactId) return;

      if (!window.confirm("Are you sure you want to revoke this employee's access?")) {
        return;
      }

      try {
        await revokeAccess({ userId, contactId: singleContactId });
        toast.success("Access revoked successfully");
      } catch (error: any) {
        toast.error(error.message || "Failed to revoke access");
      }
    },
    [singleContactId, revokeAccess]
  );

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      const clientIdsToGrant: string[] = values.grantToAllClients
        ? (displayedClients || []).map((client: ClientRecord) => client.id)
        : (values.contactIds ?? []);

      await grantAccessToMultiple(clientIdsToGrant, values.userId);
      form.setValue("userId", "");
      toast.success("Access granted successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to grant access");
    }
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-4">
      {singleContactId && (
        <div className="border rounded-lg p-4 bg-muted/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Current Employee Access</h3>
          </div>
          <p className="text-sm text-muted-foreground text-center py-2">
            No employees currently have access to this contact
          </p>
        </div>
      )}

      <div className={singleContactId ? "border-t pt-4" : ""}>
        <h3 className="text-sm font-semibold mb-4">Grant New Access</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <GrantAccessEmployeeSection
            userId={form.watch("userId")}
            onUserSelect={setUserId}
            error={form.formState.errors.userId?.message}
          />

          <ClientsFieldSection
            form={form}
            displayedClients={displayedClients}
            clientSearchTerm={clientSearchTerm}
            setClientSearchTerm={setClientSearchTerm}
            isClientSearching={isClientSearching}
            clientsLoading={clientsLoading}
            setContactIds={setContactIds}
            setGrantToAllClients={setGrantToAllClients}
            isBulk={isBulk}
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={() => closeModal("grant-employee-access")}
          disabled={grantAccessMutation.isPending}
          className="w-full sm:w-auto"
        >
          Close
        </Button>
        <Button
          type="submit"
          disabled={grantAccessMutation.isPending || clientsLoading || !form.watch("userId")}
          className="w-full sm:w-auto"
        >
          {grantAccessMutation.isPending ? "Granting..." : "Grant Access"}
        </Button>
      </div>
    </form>
  );
};
